import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth, apiFetch } from "@/lib/auth";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Users as UsersIcon,
    Store as StoreIcon,
    Package,
    Activity,
    Search,
    Trash2,
    Shield,
    LogOut,
    Plus,
    Pencil,
    Tag,
    Ban,
    Power,
    KeyRound,
    AlertTriangle,
    Check,
    X as XIcon,
    Star,
    Eye,
    EyeOff,
    Settings as SettingsIcon,
    Megaphone,
    Lock,
    Download,
    Shirt,
    Watch,
    Home as HomeIcon,
    Coffee,
    Palette,
    Sparkles,
    Baby,
    PawPrint,
    Laptop,
    Bike,
    ShoppingBag,
    Music,
    Book,
    Camera,
    Gift,
    Heart,
    Gem,
    Wrench,
    Flower2,
    Utensils,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_ICONS = {
    Shirt,
    Watch,
    Home: HomeIcon,
    Coffee,
    Palette,
    Sparkles,
    Baby,
    PawPrint,
    Laptop,
    Bike,
    ShoppingBag,
    Music,
    Book,
    Camera,
    Gift,
    Heart,
    Gem,
    Wrench,
    Flower2,
    Utensils,
};
const CATEGORY_ICON_NAMES = Object.keys(CATEGORY_ICONS);

function slugify(str) {
    return (str || "")
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

export default function AdminPage() {
    const { user, isAuthenticated, isLoading, logout } = useAuth();

    if (isLoading) return null;
    if (!isAuthenticated) return <Navigate to="/login" state={{ from: "/admin" }} replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;

    return (
        <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600 flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5" /> Administración
                    </p>
                    <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
                        Panel de Tiendify
                    </h1>
                    <p className="mt-2 text-sm text-zinc-500">
                        Hola, {user.name}. Aquí puedes ver usuarios, tiendas y productos.
                    </p>
                </div>
                <Button
                    data-testid="admin-logout"
                    onClick={logout}
                    variant="outline"
                    className="border-zinc-200"
                >
                    <LogOut className="w-4 h-4 mr-1.5" /> Cerrar sesión
                </Button>
            </div>

            <StatsBlock />

            <Tabs defaultValue="users" className="mt-12">
                <TabsList data-testid="admin-tabs" className="bg-transparent border-b border-zinc-200 rounded-none w-full justify-start h-auto p-0 gap-6">
                    <TabsTrigger
                        value="users"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        Usuarios
                    </TabsTrigger>
                    <TabsTrigger
                        value="stores"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        Tiendas
                    </TabsTrigger>
                    <TabsTrigger
                        value="products"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        Productos
                    </TabsTrigger>
                    <TabsTrigger
                        value="moderation"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        Moderación
                    </TabsTrigger>
                    <TabsTrigger
                        value="categories"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        Categorías
                    </TabsTrigger>
                    <TabsTrigger
                        value="settings"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        Configuración
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="users" className="pt-6">
                    <UsersTable />
                </TabsContent>
                <TabsContent value="stores" className="pt-6">
                    <StoresTable />
                </TabsContent>
                <TabsContent value="products" className="pt-6">
                    <ProductsTable />
                </TabsContent>
                <TabsContent value="moderation" className="pt-6">
                    <ModerationPanel />
                </TabsContent>
                <TabsContent value="categories" className="pt-6">
                    <CategoriesTable />
                </TabsContent>
                <TabsContent value="settings" className="pt-6">
                    <SettingsPanel />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function StatsBlock() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        apiFetch.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);
    if (!stats) {
        return (
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-24 rounded-xl bg-zinc-100 animate-pulse" />
                ))}
            </div>
        );
    }
    return (
        <div data-testid="admin-stats" className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat
                icon={UsersIcon}
                label="Usuarios"
                value={stats.users.total}
                hint={`${stats.users.merchants} vendedores · ${stats.users.customers} compradores`}
            />
            <Stat
                icon={StoreIcon}
                label="Tiendas"
                value={stats.stores.total}
                hint={`${stats.stores.merchant_owned} con dueño`}
            />
            <Stat
                icon={Package}
                label="Productos"
                value={stats.products.total}
                hint={
                    stats.products.pending
                        ? `${stats.products.pending} en revisión`
                        : "Sin pendientes de revisión"
                }
            />
            <Stat
                icon={Activity}
                label="Sesiones activas"
                value={stats.active_sessions}
            />
        </div>
    );
}

function Stat({ icon: Icon, label, value, hint }) {
    return (
        <div className="rounded-xl border border-zinc-200 bg-white p-5">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 grid place-items-center">
                    <Icon className="w-4 h-4 text-indigo-600" />
                </div>
                <div className="text-xs uppercase tracking-wider font-semibold text-zinc-500">
                    {label}
                </div>
            </div>
            <div className="mt-3 text-3xl font-bold text-zinc-900">{value}</div>
            {hint && <div className="mt-1 text-xs text-zinc-500">{hint}</div>}
        </div>
    );
}

function UsersTable() {
    const { user: currentUser } = useAuth();
    const [data, setData] = useState({ items: [], total: 0 });
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [sessionsFor, setSessionsFor] = useState(null);
    const [confirming, setConfirming] = useState(null);

    const load = async (query = "") => {
        setLoading(true);
        try {
            const { data: r } = await apiFetch.get("/admin/users", {
                params: { q: query || undefined, limit: 100 },
            });
            setData(r);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const onDelete = (u) =>
        setConfirming({
            title: "¿Eliminar usuario?",
            description: `${u.name || u.email} se eliminará permanentemente. Esta acción no se puede deshacer.`,
            confirmLabel: "Eliminar",
            confirmingLabel: "Eliminando…",
            tone: "destructive",
            onConfirm: async () => {
                try {
                    await apiFetch.delete(`/admin/users/${u.user_id}`);
                    toast.success("Usuario eliminado");
                    load(q);
                } catch (e) {
                    toast.error(e.response?.data?.detail || "No se pudo eliminar");
                    throw e;
                }
            },
        });

    const onRoleChange = (u, newRole) => {
        if (newRole === u.role) return;
        setConfirming({
            title: "¿Cambiar el rol?",
            description: `${u.name || u.email} pasará de "${u.role}" a "${newRole}".`,
            confirmLabel: "Cambiar rol",
            confirmingLabel: "Cambiando…",
            tone: newRole === "admin" ? "destructive" : "default",
            onConfirm: async () => {
                try {
                    await apiFetch.put(`/admin/users/${u.user_id}/role`, { role: newRole });
                    toast.success("Rol actualizado");
                    load(q);
                } catch (e) {
                    toast.error(e.response?.data?.detail || "No se pudo cambiar el rol");
                    throw e;
                }
            },
        });
    };

    const onToggleDisable = (u) => {
        if (u.disabled) {
            setConfirming({
                title: "¿Reactivar usuario?",
                description: `${u.name || u.email} podrá volver a iniciar sesión y sus tiendas/productos volverán a aparecer en el sitio.`,
                confirmLabel: "Reactivar",
                confirmingLabel: "Reactivando…",
                onConfirm: async () => {
                    try {
                        await apiFetch.post(`/admin/users/${u.user_id}/enable`);
                        toast.success("Usuario reactivado");
                        load(q);
                    } catch (e) {
                        toast.error(e.response?.data?.detail || "No se pudo reactivar");
                        throw e;
                    }
                },
            });
            return;
        }
        setConfirming({
            title: "¿Deshabilitar usuario?",
            description: `Vas a deshabilitar a ${u.name || u.email}. Sus tiendas y productos dejarán de aparecer en el sitio y todas sus sesiones serán revocadas.`,
            confirmLabel: "Deshabilitar",
            confirmingLabel: "Deshabilitando…",
            tone: "destructive",
            reason: {
                label: "Motivo (opcional)",
                placeholder: "Ej. Contenido reportado, cuenta comprometida…",
            },
            onConfirm: async (reason) => {
                try {
                    await apiFetch.post(`/admin/users/${u.user_id}/disable`, { reason });
                    toast.success("Usuario deshabilitado");
                    load(q);
                } catch (e) {
                    toast.error(e.response?.data?.detail || "No se pudo deshabilitar");
                    throw e;
                }
            },
        });
    };

    return (
        <div data-testid="admin-users-table">
            <div className="flex items-center gap-3 justify-between flex-wrap">
                <SearchInput
                    value={q}
                    onChange={setQ}
                    onSubmit={() => load(q)}
                    placeholder="Buscar por nombre o correo…"
                    testid="admin-users-search"
                />
                <ExportCsvButton kind="users" q={q} />
            </div>
            <p className="mt-3 text-xs text-zinc-500">{data.total} usuarios</p>
            <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Usuario</th>
                            <th className="text-left px-4 py-3">Correo</th>
                            <th className="text-left px-4 py-3">Rol</th>
                            <th className="text-left px-4 py-3">Estado</th>
                            <th className="text-left px-4 py-3">Registrado</th>
                            <th className="text-right px-4 py-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">Cargando…</td>
                            </tr>
                        ) : data.items.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-4 py-8 text-center text-zinc-400">Sin resultados</td>
                            </tr>
                        ) : (
                            data.items.map((u) => {
                                const isSelf = currentUser?.user_id === u.user_id;
                                return (
                                    <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className={u.disabled ? "bg-red-50/40" : ""}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                {u.picture ? (
                                                    <img src={u.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
                                                ) : (
                                                    <div className="w-7 h-7 rounded-full bg-indigo-100 grid place-items-center text-xs font-semibold text-indigo-700">
                                                        {(u.name || u.email)[0]?.toUpperCase()}
                                                    </div>
                                                )}
                                                <span className="font-medium text-zinc-900">{u.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-zinc-600">{u.email}</td>
                                        <td className="px-4 py-3">
                                            <select
                                                data-testid={`admin-role-${u.user_id}`}
                                                value={u.role}
                                                onChange={(e) => onRoleChange(u, e.target.value)}
                                                disabled={isSelf}
                                                className="h-8 rounded border border-zinc-200 bg-white px-2 text-xs"
                                            >
                                                <option value="customer">customer</option>
                                                <option value="merchant">merchant</option>
                                                <option value="admin">admin</option>
                                            </select>
                                        </td>
                                        <td className="px-4 py-3">
                                            {u.disabled ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border bg-red-100 text-red-700 border-red-200">
                                                    <Ban className="w-3 h-3" /> Deshabilitado
                                                </span>
                                            ) : (
                                                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    Activo
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-zinc-500 text-xs">
                                            {new Date(u.created_at).toLocaleDateString("es-MX")}
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    data-testid={`admin-sessions-${u.user_id}`}
                                                    onClick={() => setSessionsFor(u)}
                                                    className="text-zinc-400 hover:text-indigo-600"
                                                    aria-label="Sesiones"
                                                    title="Ver sesiones"
                                                >
                                                    <KeyRound className="w-4 h-4" />
                                                </button>
                                                {!isSelf && u.role !== "admin" && (
                                                    <button
                                                        data-testid={`admin-toggle-disable-${u.user_id}`}
                                                        onClick={() => onToggleDisable(u)}
                                                        className={u.disabled ? "text-emerald-500 hover:text-emerald-700" : "text-zinc-400 hover:text-amber-600"}
                                                        aria-label={u.disabled ? "Reactivar" : "Deshabilitar"}
                                                        title={u.disabled ? "Reactivar" : "Deshabilitar"}
                                                    >
                                                        {u.disabled ? <Power className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                                                    </button>
                                                )}
                                                {!isSelf && u.role !== "admin" && (
                                                    <button
                                                        data-testid={`admin-delete-user-${u.user_id}`}
                                                        onClick={() => onDelete(u)}
                                                        className="text-zinc-400 hover:text-red-600"
                                                        aria-label="Eliminar"
                                                        title="Eliminar"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <UserSessionsDialog
                user={sessionsFor}
                onClose={() => setSessionsFor(null)}
                onRevoked={() => load(q)}
            />

            <ConfirmDialog
                open={!!confirming}
                onOpenChange={(o) => !o && setConfirming(null)}
                {...(confirming || {})}
            />
        </div>
    );
}

function UserSessionsDialog({ user, onClose, onRevoked }) {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [askingRevoke, setAskingRevoke] = useState(false);

    useEffect(() => {
        if (!user) return;
        setLoading(true);
        apiFetch
            .get(`/admin/users/${user.user_id}/sessions`)
            .then((r) => setSessions(r.data.items || []))
            .catch(() => setSessions([]))
            .finally(() => setLoading(false));
    }, [user]);

    const revoke = async () => {
        try {
            const { data } = await apiFetch.delete(`/admin/users/${user.user_id}/sessions`);
            toast.success(`${data.revoked} sesión(es) cerradas`);
            setSessions([]);
            onRevoked?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo cerrar las sesiones");
            throw e;
        }
    };

    return (
        <>
        <Dialog open={!!user} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Sesiones activas</DialogTitle>
                    <DialogDescription>
                        {user?.name || user?.email} tiene {sessions.length} sesión(es) abierta(s).
                    </DialogDescription>
                </DialogHeader>
                <div className="max-h-72 overflow-y-auto -mx-2 px-2">
                    {loading ? (
                        <p className="text-sm text-zinc-400 py-6 text-center">Cargando…</p>
                    ) : sessions.length === 0 ? (
                        <p className="text-sm text-zinc-400 py-6 text-center">Sin sesiones activas.</p>
                    ) : (
                        <ul className="space-y-2">
                            {sessions.map((s, i) => (
                                <li key={i} className="rounded-lg border border-zinc-200 px-3 py-2 text-xs text-zinc-600">
                                    <div className="font-medium text-zinc-800">
                                        Creada: {new Date(s.created_at).toLocaleString("es-MX")}
                                    </div>
                                    <div className="text-zinc-500">
                                        Expira: {new Date(s.expires_at).toLocaleString("es-MX")}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cerrar</Button>
                    {sessions.length > 0 && (
                        <Button
                            data-testid="admin-revoke-sessions"
                            onClick={() => setAskingRevoke(true)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                        >
                            <AlertTriangle className="w-4 h-4 mr-1.5" /> Revocar todas
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>

        <ConfirmDialog
            open={askingRevoke}
            onOpenChange={setAskingRevoke}
            title="¿Cerrar todas las sesiones?"
            description={`Las ${sessions.length} sesión(es) abiertas de ${user?.name || user?.email} se cerrarán inmediatamente. El usuario tendrá que iniciar sesión de nuevo.`}
            confirmLabel="Cerrar sesiones"
            confirmingLabel="Cerrando…"
            tone="destructive"
            onConfirm={revoke}
        />
        </>
    );
}

function StoresTable() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [categories, setCategories] = useState([]);
    const [confirming, setConfirming] = useState(null);

    const load = async (query = "") => {
        setLoading(true);
        try {
            const { data: r } = await apiFetch.get("/admin/stores", {
                params: { q: query || undefined, limit: 100 },
            });
            setData(r);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        apiFetch.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    }, []);

    const onDelete = (s) => setConfirming({
        title: "¿Eliminar tienda?",
        description: `Se borrará la tienda "${s.name}" junto con sus ${s.productsCount || 0} producto(s). Esta acción no se puede deshacer.`,
        confirmLabel: "Eliminar tienda",
        confirmingLabel: "Eliminando…",
        tone: "destructive",
        onConfirm: async () => {
            try {
                const { data: r } = await apiFetch.delete(`/admin/stores/${s.id}`);
                toast.success(`Tienda eliminada (${r.deleted_products} productos)`);
                load(q);
            } catch (e) {
                toast.error(e.response?.data?.detail || "No se pudo eliminar");
                throw e;
            }
        },
    });

    return (
        <div data-testid="admin-stores-table">
            <div className="flex items-center gap-3 justify-between flex-wrap">
                <SearchInput
                    value={q}
                    onChange={setQ}
                    onSubmit={() => load(q)}
                    placeholder="Buscar por nombre o categoría…"
                    testid="admin-stores-search"
                />
                <ExportCsvButton kind="stores" q={q} />
            </div>
            <p className="mt-3 text-xs text-zinc-500">{data.total} tiendas</p>
            <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Tienda</th>
                            <th className="text-left px-4 py-3">Categoría</th>
                            <th className="text-left px-4 py-3">Ciudad</th>
                            <th className="text-left px-4 py-3">Productos</th>
                            <th className="text-left px-4 py-3">Destacada</th>
                            <th className="text-left px-4 py-3">Orden</th>
                            <th className="text-right px-4 py-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                        {loading ? (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400">Cargando…</td></tr>
                        ) : data.items.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400">Sin resultados</td></tr>
                        ) : data.items.map((s) => (
                            <tr key={s.id} data-testid={`admin-store-row-${s.slug}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <img src={s.logo} alt="" className="w-7 h-7 rounded-full object-cover" />
                                        <div>
                                            <div className="font-medium text-zinc-900">{s.name}</div>
                                            <div className="text-xs text-zinc-400 font-mono">{s.slug}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-zinc-600 capitalize">{s.category}</td>
                                <td className="px-4 py-3 text-zinc-600">{s.location?.city}</td>
                                <td className="px-4 py-3 text-zinc-600">{s.productsCount}</td>
                                <td className="px-4 py-3">
                                    <FeaturedToggle store={s} onChanged={() => load(q)} />
                                </td>
                                <td className="px-4 py-3">
                                    {s.featured ? (
                                        <FeaturedOrderInput store={s} onChanged={() => load(q)} />
                                    ) : (
                                        <span className="text-xs text-zinc-300">—</span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <button
                                            data-testid={`admin-edit-store-${s.slug}`}
                                            onClick={() => setEditing(s)}
                                            className="text-zinc-400 hover:text-indigo-600"
                                            aria-label="Editar"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            data-testid={`admin-delete-store-${s.slug}`}
                                            onClick={() => onDelete(s)}
                                            className="text-zinc-400 hover:text-red-600"
                                            aria-label="Eliminar"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <StoreEditDialog
                store={editing}
                categories={categories}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    load(q);
                }}
            />

            <ConfirmDialog
                open={!!confirming}
                onOpenChange={(o) => !o && setConfirming(null)}
                {...(confirming || {})}
            />
        </div>
    );
}

function StoreEditDialog({ store, categories, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!store) return;
        setForm({
            name: store.name || "",
            slug: store.slug || "",
            shortDescription: store.shortDescription || "",
            description: store.description || "",
            category: store.category || "",
            featured: !!store.featured,
            featuredOrder: store.featuredOrder ?? "",
        });
    }, [store]);

    const set = (k) => (e) => {
        const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
        setForm((prev) => ({ ...prev, [k]: v }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name?.trim()) return toast.error("El nombre es obligatorio");
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                slug: form.slug.trim() || undefined,
                shortDescription: form.shortDescription.trim(),
                description: form.description.trim(),
                category: form.category.trim(),
                featured: !!form.featured,
            };
            if (form.featured && form.featuredOrder !== "") {
                payload.featuredOrder = parseInt(form.featuredOrder, 10);
            }
            await apiFetch.put(`/admin/stores/${store.id}`, payload);
            toast.success("Tienda actualizada");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!store} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>Editar tienda</DialogTitle>
                    <DialogDescription>
                        Cambios reflejados en el sitio público inmediatamente. Al cambiar el slug se actualizan los productos asociados.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="st-name">Nombre</Label>
                            <Input id="st-name" value={form.name || ""} onChange={set("name")} maxLength={80} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="st-slug">Slug</Label>
                            <Input id="st-slug" value={form.slug || ""} onChange={set("slug")} maxLength={80} />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="st-cat">Categoría</Label>
                        <select
                            id="st-cat"
                            value={form.category || ""}
                            onChange={set("category")}
                            className="w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                        >
                            <option value="">— Sin categoría —</option>
                            {categories.map((c) => (
                                <option key={c.slug} value={c.slug}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="st-short">Descripción corta</Label>
                        <Input id="st-short" value={form.shortDescription || ""} onChange={set("shortDescription")} maxLength={200} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="st-desc">Descripción</Label>
                        <Textarea id="st-desc" value={form.description || ""} onChange={set("description")} maxLength={2000} rows={4} />
                    </div>
                    <div className="flex items-center gap-6 text-sm">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!!form.featured} onChange={set("featured")} />
                            Tienda destacada en la portada
                        </label>
                        {form.featured && (
                            <div className="flex items-center gap-2">
                                <Label htmlFor="st-order" className="text-xs">Orden</Label>
                                <Input
                                    id="st-order"
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={form.featuredOrder ?? ""}
                                    onChange={set("featuredOrder")}
                                    placeholder="(menor = arriba)"
                                    className="w-32 h-8"
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
                        <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                            {saving ? "Guardando…" : "Guardar cambios"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function FeaturedToggle({ store, onChanged }) {
    const [busy, setBusy] = useState(false);
    const toggle = async () => {
        setBusy(true);
        try {
            await apiFetch.put(`/admin/stores/${store.id}`, {
                featured: !store.featured,
            });
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo actualizar");
        } finally {
            setBusy(false);
        }
    };
    return (
        <button
            data-testid={`admin-featured-toggle-${store.slug}`}
            onClick={toggle}
            disabled={busy}
            className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border transition ${
                store.featured
                    ? "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                    : "bg-zinc-50 text-zinc-500 border-zinc-200 hover:bg-zinc-100"
            }`}
            aria-pressed={store.featured}
        >
            <Star className={`w-3 h-3 ${store.featured ? "fill-current" : ""}`} />
            {store.featured ? "Sí" : "No"}
        </button>
    );
}

function FeaturedOrderInput({ store, onChanged }) {
    const [val, setVal] = useState(store.featuredOrder ?? "");
    useEffect(() => {
        setVal(store.featuredOrder ?? "");
    }, [store.featuredOrder]);
    const save = async () => {
        const order = val === "" ? null : parseInt(val, 10);
        if (order === store.featuredOrder) return;
        if (order !== null && (isNaN(order) || order < 0)) {
            toast.error("Orden inválido");
            setVal(store.featuredOrder ?? "");
            return;
        }
        try {
            await apiFetch.put(`/admin/stores/${store.id}`, {
                featuredOrder: order ?? 0,
            });
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo guardar");
            setVal(store.featuredOrder ?? "");
        }
    };
    return (
        <input
            type="number"
            min="0"
            step="1"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            placeholder="0"
            className="w-16 h-8 rounded border border-zinc-200 bg-white px-2 text-sm text-zinc-700"
            data-testid={`admin-featured-order-${store.slug}`}
        />
    );
}

function ProductStatusBadge({ status }) {
    const s = status || "approved";
    if (s === "approved") {
        return (
            <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
                Aprobado
            </span>
        );
    }
    if (s === "pending") {
        return (
            <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border bg-amber-50 text-amber-700 border-amber-200">
                Pendiente
            </span>
        );
    }
    return (
        <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border bg-red-50 text-red-700 border-red-200">
            Rechazado
        </span>
    );
}

function ModerationPanel() {
    const [settings, setSettings] = useState({});
    const [statusFilter, setStatusFilter] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirming, setConfirming] = useState(null);

    const loadSettings = async () => {
        try {
            const { data } = await apiFetch.get("/admin/settings");
            setSettings(data.items || {});
        } catch {}
    };
    const loadItems = async (status) => {
        setLoading(true);
        try {
            const { data } = await apiFetch.get("/admin/products", {
                params: { status, limit: 200 },
            });
            setItems(data.items || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        loadSettings();
    }, []);
    useEffect(() => {
        loadItems(statusFilter);
    }, [statusFilter]);

    const toggleApproval = async () => {
        const next = !settings.requireProductApproval;
        try {
            await apiFetch.put("/admin/settings/requireProductApproval", { value: next });
            setSettings((p) => ({ ...p, requireProductApproval: next }));
            toast.success(next ? "Aprobación previa activada" : "Aprobación previa desactivada");
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo guardar");
        }
    };

    const approve = async (p) => {
        try {
            await apiFetch.post(`/admin/products/${p.id}/approve`);
            toast.success("Producto aprobado");
            loadItems(statusFilter);
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo aprobar");
        }
    };
    const reject = (p) => setConfirming({
        title: "¿Rechazar producto?",
        description: `"${p.name}" dejará de aparecer en el sitio público. Puedes agregar un motivo para que el comerciante lo vea.`,
        confirmLabel: "Rechazar",
        confirmingLabel: "Rechazando…",
        tone: "destructive",
        reason: {
            label: "Motivo (opcional)",
            placeholder: "Ej. Imagen inapropiada, info incorrecta…",
        },
        onConfirm: async (reason) => {
            try {
                await apiFetch.post(`/admin/products/${p.id}/reject`, { reason });
                toast.success("Producto rechazado");
                loadItems(statusFilter);
            } catch (e) {
                toast.error(e.response?.data?.detail || "No se pudo rechazar");
                throw e;
            }
        },
    });

    return (
        <div data-testid="admin-moderation-panel" className="space-y-6">
            <div className="rounded-xl border border-zinc-200 bg-white p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4 text-amber-500" />
                            Aprobación previa de productos
                        </h3>
                        <p className="mt-1 text-xs text-zinc-500 max-w-xl">
                            Cuando está activa, los productos nuevos de los comerciantes entran como
                            <span className="font-semibold"> pendientes</span> y no aparecen en el sitio
                            hasta que un admin los aprueba.
                        </p>
                    </div>
                    <button
                        data-testid="admin-toggle-approval"
                        onClick={toggleApproval}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                            settings.requireProductApproval
                                ? "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700"
                                : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
                        }`}
                        aria-pressed={!!settings.requireProductApproval}
                    >
                        {settings.requireProductApproval ? (
                            <><Eye className="w-3.5 h-3.5" /> Activa</>
                        ) : (
                            <><EyeOff className="w-3.5 h-3.5" /> Inactiva</>
                        )}
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-2 text-sm">
                <span className="text-zinc-500">Mostrar:</span>
                {[
                    { v: "pending", label: "Pendientes" },
                    { v: "rejected", label: "Rechazados" },
                    { v: "approved", label: "Aprobados" },
                ].map((opt) => (
                    <button
                        key={opt.v}
                        onClick={() => setStatusFilter(opt.v)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                            statusFilter === opt.v
                                ? "bg-zinc-900 text-white border-zinc-900"
                                : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"
                        }`}
                        data-testid={`admin-mod-filter-${opt.v}`}
                    >
                        {opt.label}
                    </button>
                ))}
                <span className="ml-auto text-xs text-zinc-400">{items.length} resultado(s)</span>
            </div>

            {loading ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="h-56 rounded-xl bg-zinc-100 animate-pulse" />
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="rounded-xl border border-dashed border-zinc-200 bg-white p-12 text-center text-sm text-zinc-400">
                    No hay productos en este estado.
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((p) => (
                        <div
                            key={p.id}
                            data-testid={`admin-mod-item-${p.slug}`}
                            className="rounded-xl border border-zinc-200 bg-white overflow-hidden flex flex-col"
                        >
                            <div className="aspect-video bg-zinc-50">
                                {p.images?.[0] && (
                                    <img src={p.images[0]} alt="" className="w-full h-full object-cover" />
                                )}
                            </div>
                            <div className="p-4 flex-1 flex flex-col">
                                <div className="flex items-start justify-between gap-2">
                                    <h4 className="text-sm font-semibold text-zinc-900 line-clamp-2">{p.name}</h4>
                                    <ProductStatusBadge status={p.status} />
                                </div>
                                <p className="mt-1 text-xs text-zinc-500">{p.storeName} · ${p.price?.toLocaleString("es-MX")}</p>
                                {p.shortDescription && (
                                    <p className="mt-2 text-xs text-zinc-600 line-clamp-2">{p.shortDescription}</p>
                                )}
                                {p.rejectedReason && (
                                    <p className="mt-2 text-xs text-red-600 italic">Motivo: {p.rejectedReason}</p>
                                )}
                                <div className="mt-auto pt-3 flex items-center gap-2">
                                    {p.status !== "approved" && (
                                        <button
                                            data-testid={`admin-mod-approve-${p.slug}`}
                                            onClick={() => approve(p)}
                                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
                                        >
                                            <Check className="w-3.5 h-3.5" /> Aprobar
                                        </button>
                                    )}
                                    {p.status !== "rejected" && (
                                        <button
                                            data-testid={`admin-mod-reject-${p.slug}`}
                                            onClick={() => reject(p)}
                                            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-md bg-red-600 text-white hover:bg-red-700"
                                        >
                                            <XIcon className="w-3.5 h-3.5" /> Rechazar
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmDialog
                open={!!confirming}
                onOpenChange={(o) => !o && setConfirming(null)}
                {...(confirming || {})}
            />
        </div>
    );
}

function ProductsTable() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [categories, setCategories] = useState([]);
    const [confirming, setConfirming] = useState(null);

    const load = async (query = "") => {
        setLoading(true);
        try {
            const { data: r } = await apiFetch.get("/admin/products", {
                params: { q: query || undefined, limit: 200 },
            });
            setData(r);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
        apiFetch.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
    }, []);

    const onDelete = (p) => setConfirming({
        title: "¿Eliminar producto?",
        description: `"${p.name}" se eliminará permanentemente. Esta acción no se puede deshacer.`,
        confirmLabel: "Eliminar",
        confirmingLabel: "Eliminando…",
        tone: "destructive",
        onConfirm: async () => {
            try {
                await apiFetch.delete(`/admin/products/${p.id}`);
                toast.success("Producto eliminado");
                load(q);
            } catch (e) {
                toast.error(e.response?.data?.detail || "No se pudo eliminar");
                throw e;
            }
        },
    });

    return (
        <div data-testid="admin-products-table">
            <div className="flex items-center gap-3 justify-between flex-wrap">
                <SearchInput
                    value={q}
                    onChange={setQ}
                    onSubmit={() => load(q)}
                    placeholder="Buscar producto…"
                    testid="admin-products-search"
                />
                <ExportCsvButton kind="products" q={q} />
            </div>
            <p className="mt-3 text-xs text-zinc-500">{data.total} productos</p>
            <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Producto</th>
                            <th className="text-left px-4 py-3">Tienda</th>
                            <th className="text-left px-4 py-3">Categoría</th>
                            <th className="text-left px-4 py-3">Precio</th>
                            <th className="text-left px-4 py-3">Stock</th>
                            <th className="text-left px-4 py-3">Estado</th>
                            <th className="text-right px-4 py-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                        {loading ? (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400">Cargando…</td></tr>
                        ) : data.items.length === 0 ? (
                            <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-400">Sin resultados</td></tr>
                        ) : data.items.map((p) => (
                            <tr key={p.id} data-testid={`admin-product-row-${p.slug}`}>
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-3">
                                        <img src={p.images?.[0]} alt="" className="w-9 h-9 rounded object-cover" />
                                        <div>
                                            <div className="font-medium text-zinc-900">{p.name}</div>
                                            <div className="text-xs text-zinc-400 font-mono">{p.slug}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-zinc-600">{p.storeName}</td>
                                <td className="px-4 py-3 text-zinc-600 capitalize">{p.category}</td>
                                <td className="px-4 py-3 text-zinc-600">${p.price?.toLocaleString("es-MX")}</td>
                                <td className="px-4 py-3 text-zinc-600">{p.stock}</td>
                                <td className="px-4 py-3"><ProductStatusBadge status={p.status} /></td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <button
                                            data-testid={`admin-edit-product-${p.slug}`}
                                            onClick={() => setEditing(p)}
                                            className="text-zinc-400 hover:text-indigo-600"
                                            aria-label="Editar"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </button>
                                        <button
                                            data-testid={`admin-delete-product-${p.slug}`}
                                            onClick={() => onDelete(p)}
                                            className="text-zinc-400 hover:text-red-600"
                                            aria-label="Eliminar"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ProductEditDialog
                product={editing}
                categories={categories}
                onClose={() => setEditing(null)}
                onSaved={() => {
                    setEditing(null);
                    load(q);
                }}
            />

            <ConfirmDialog
                open={!!confirming}
                onOpenChange={(o) => !o && setConfirming(null)}
                {...(confirming || {})}
            />
        </div>
    );
}

function ProductEditDialog({ product, categories, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!product) return;
        const dims = product.dimensions || {};
        setForm({
            name: product.name || "",
            slug: product.slug || "",
            shortDescription: product.shortDescription || "",
            description: product.description || "",
            category: product.category || "",
            price: product.price ?? "",
            originalPrice: product.originalPrice ?? "",
            stock: product.stock ?? 0,
            isNew: !!product.isNew,
            isFeatured: !!product.isFeatured,
            weight: product.weight ?? "",
            dimLength: dims.length ?? "",
            dimWidth: dims.width ?? "",
            dimHeight: dims.height ?? "",
            tags: Array.isArray(product.tags) ? product.tags.join(", ") : "",
        });
    }, [product]);

    const set = (k) => (e) => {
        const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
        setForm((prev) => ({ ...prev, [k]: v }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name?.trim()) return toast.error("El nombre es obligatorio");
        const price = parseFloat(form.price);
        if (isNaN(price) || price < 0) return toast.error("Precio inválido");
        const dimsFilled = [form.dimLength, form.dimWidth, form.dimHeight].filter(
            (d) => d !== "" && d != null
        ).length;
        if (dimsFilled > 0 && dimsFilled < 3) {
            return toast.error("Si pones medidas, ingresa las 3 (largo, ancho, alto)");
        }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                slug: form.slug.trim() || undefined,
                shortDescription: form.shortDescription.trim(),
                description: form.description.trim(),
                category: form.category.trim(),
                price,
                stock: parseInt(form.stock, 10) || 0,
                isNew: !!form.isNew,
                isFeatured: !!form.isFeatured,
            };
            if (form.originalPrice !== "" && form.originalPrice != null) {
                payload.originalPrice = parseFloat(form.originalPrice);
            }
            if (form.weight !== "" && form.weight != null) {
                const w = parseFloat(form.weight);
                if (!isNaN(w) && w >= 0) payload.weight = w;
            }
            if (dimsFilled === 3) {
                payload.dimensions = {
                    length: parseFloat(form.dimLength),
                    width: parseFloat(form.dimWidth),
                    height: parseFloat(form.dimHeight),
                };
            }
            payload.tags = (form.tags || "")
                .split(",")
                .map((t) => t.trim().toLowerCase())
                .filter(Boolean);
            await apiFetch.put(`/admin/products/${product.id}`, payload);
            toast.success("Producto actualizado");
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    const images = Array.isArray(product?.images) ? product.images : [];
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const createdAt = product?.createdAt ? new Date(product.createdAt) : null;

    return (
        <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Detalle del producto</DialogTitle>
                    <DialogDescription>
                        Vista completa para administrador. Las imágenes y variantes se gestionan desde el panel del comerciante.
                    </DialogDescription>
                </DialogHeader>

                {product && (
                    <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-3 text-xs text-zinc-600 space-y-1.5">
                        <div className="flex flex-wrap gap-x-6 gap-y-1">
                            <span><b className="text-zinc-900">Tienda:</b> {product.storeName} <span className="font-mono text-zinc-400">/{product.storeSlug}</span></span>
                            <span><b className="text-zinc-900">ID:</b> <span className="font-mono">{product.id}</span></span>
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-1">
                            {createdAt && (
                                <span><b className="text-zinc-900">Creado:</b> {createdAt.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}</span>
                            )}
                            <span><b className="text-zinc-900">Ventas:</b> {product.salesCount ?? 0}</span>
                            <span><b className="text-zinc-900">Rating:</b> {product.rating ?? 0} ({product.reviewsCount ?? 0})</span>
                        </div>
                    </div>
                )}

                {images.length > 0 && (
                    <div>
                        <Label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">Imágenes ({images.length})</Label>
                        <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                            {images.map((src, i) => (
                                <a
                                    key={i}
                                    href={src}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="shrink-0 block w-20 h-20 rounded-lg overflow-hidden border border-zinc-200 bg-zinc-100 hover:ring-2 hover:ring-indigo-500"
                                    title={`Abrir imagen ${i + 1}`}
                                >
                                    <img src={src} alt={`img-${i}`} className="w-full h-full object-cover" />
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="pr-name">Nombre</Label>
                            <Input id="pr-name" value={form.name || ""} onChange={set("name")} maxLength={120} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="pr-slug">Slug</Label>
                            <Input id="pr-slug" value={form.slug || ""} onChange={set("slug")} maxLength={120} />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="pr-cat">Categoría</Label>
                        <select
                            id="pr-cat"
                            value={form.category || ""}
                            onChange={set("category")}
                            className="w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                        >
                            <option value="">— Sin categoría —</option>
                            {categories.map((c) => (
                                <option key={c.slug} value={c.slug}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="pr-price">Precio</Label>
                            <Input id="pr-price" type="number" min="0" step="0.01" value={form.price ?? ""} onChange={set("price")} required />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="pr-original">Precio original</Label>
                            <Input id="pr-original" type="number" min="0" step="0.01" value={form.originalPrice ?? ""} onChange={set("originalPrice")} placeholder="(opcional)" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="pr-stock">Stock</Label>
                            <Input id="pr-stock" type="number" min="0" step="1" value={form.stock ?? 0} onChange={set("stock")} />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="pr-short">Descripción corta</Label>
                        <Input id="pr-short" value={form.shortDescription || ""} onChange={set("shortDescription")} maxLength={200} />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="pr-desc">Descripción</Label>
                        <Textarea id="pr-desc" value={form.description || ""} onChange={set("description")} maxLength={4000} rows={4} />
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!!form.isNew} onChange={set("isNew")} />
                            Marcar como nuevo
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="checkbox" checked={!!form.isFeatured} onChange={set("isFeatured")} />
                            Destacado
                        </label>
                    </div>

                    <div className="pt-2 border-t border-zinc-200">
                        <Label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                            Logística (envío)
                        </Label>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-4 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="pr-weight" className="text-xs">Peso (kg)</Label>
                                <Input id="pr-weight" type="number" min="0" step="0.01" value={form.weight ?? ""} onChange={set("weight")} placeholder="1.0" />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="pr-dim-l" className="text-xs">Largo (cm)</Label>
                                <Input id="pr-dim-l" type="number" min="0" step="0.1" value={form.dimLength ?? ""} onChange={set("dimLength")} />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="pr-dim-w" className="text-xs">Ancho (cm)</Label>
                                <Input id="pr-dim-w" type="number" min="0" step="0.1" value={form.dimWidth ?? ""} onChange={set("dimWidth")} />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="pr-dim-h" className="text-xs">Alto (cm)</Label>
                                <Input id="pr-dim-h" type="number" min="0" step="0.1" value={form.dimHeight ?? ""} onChange={set("dimHeight")} />
                            </div>
                        </div>
                        <p className="mt-2 text-[11px] text-zinc-500">
                            Si pones medidas, ingresa las 3 (largo, ancho, alto). Se usan para cotizar guías de envío.
                        </p>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="pr-tags" className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                            Tags (separados por coma)
                        </Label>
                        <Input
                            id="pr-tags"
                            value={form.tags ?? ""}
                            onChange={set("tags")}
                            placeholder="hecho a mano, lana, oaxaca"
                            maxLength={400}
                        />
                    </div>

                    {variants.length > 0 && (
                        <div className="pt-2 border-t border-zinc-200">
                            <Label className="text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                                Variantes
                            </Label>
                            <div className="mt-2 space-y-2">
                                {variants.map((v, i) => (
                                    <div key={i} className="rounded-md border border-zinc-200 bg-zinc-50/60 px-3 py-2 text-sm">
                                        <div className="font-medium text-zinc-900">{v.name}</div>
                                        <div className="mt-1 flex flex-wrap gap-1.5">
                                            {(v.options || []).map((opt, j) => (
                                                <span key={j} className="px-2 py-0.5 text-xs rounded-full bg-white border border-zinc-200 text-zinc-700">
                                                    {opt}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-2 text-[11px] text-zinc-500">
                                Sólo lectura. Pide al comerciante editar variantes desde su panel.
                            </p>
                        </div>
                    )}

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
                        <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                            {saving ? "Guardando…" : "Guardar cambios"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function SettingsPanel() {
    const [reserved, setReserved] = useState([]);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await apiFetch.get("/admin/settings");
            const items = data.items || {};
            setReserved(Array.isArray(items.reserved_slugs) ? items.reserved_slugs : []);
            setBanners(Array.isArray(items.banners) ? items.banners : []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    if (loading) {
        return (
            <div className="space-y-4">
                <div className="h-40 rounded-xl bg-zinc-100 animate-pulse" />
                <div className="h-64 rounded-xl bg-zinc-100 animate-pulse" />
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <ReservedSlugsEditor value={reserved} onChange={setReserved} onSaved={load} />
            <BannersEditor value={banners} onChange={setBanners} onSaved={load} />
        </div>
    );
}

function ReservedSlugsEditor({ value, onChange, onSaved }) {
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);

    const add = () => {
        const s = draft
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9-]/g, "");
        if (!s) return;
        if (value.includes(s)) {
            toast.error("Ya está en la lista");
            return;
        }
        onChange([...value, s]);
        setDraft("");
    };
    const remove = (s) => onChange(value.filter((v) => v !== s));

    const save = async () => {
        setSaving(true);
        try {
            await apiFetch.put("/admin/settings/reserved_slugs", { value });
            toast.success("Slugs reservados guardados");
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="admin-reserved-slugs">
            <header className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-zinc-500" />
                <h3 className="text-sm font-semibold text-zinc-900">Slugs reservados extra</h3>
            </header>
            <p className="mt-1 text-xs text-zinc-500">
                Identificadores que ningún comerciante podrá usar como URL de tienda. La
                lista base (rutas internas, archivos estáticos) está hardcoded; aquí
                puedes añadir nombres propios (marca, palabras prohibidas, etc.).
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
                {value.length === 0 && (
                    <span className="text-xs text-zinc-400 italic">Sin slugs extra.</span>
                )}
                {value.map((s) => (
                    <span
                        key={s}
                        className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 text-xs font-mono rounded-full bg-zinc-100 text-zinc-700 border border-zinc-200"
                        data-testid={`reserved-chip-${s}`}
                    >
                        {s}
                        <button
                            onClick={() => remove(s)}
                            aria-label={`Quitar ${s}`}
                            className="rounded-full p-0.5 hover:bg-zinc-200"
                        >
                            <XIcon className="w-3 h-3" />
                        </button>
                    </span>
                ))}
            </div>
            <div className="mt-4 flex gap-2">
                <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            add();
                        }
                    }}
                    placeholder="ej. tiendify, mi-marca"
                    maxLength={40}
                    className="flex-1 h-10"
                    data-testid="reserved-slug-input"
                />
                <Button type="button" variant="outline" onClick={add}>
                    <Plus className="w-4 h-4 mr-1.5" /> Añadir
                </Button>
            </div>
            <div className="mt-4 flex justify-end">
                <Button
                    onClick={save}
                    disabled={saving}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="save-reserved-slugs"
                >
                    {saving ? "Guardando…" : "Guardar lista"}
                </Button>
            </div>
        </section>
    );
}

function BannersEditor({ value, onChange, onSaved }) {
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const upsert = (banner) => {
        const idx = value.findIndex((b) => b.id === banner.id);
        if (idx === -1) onChange([...value, banner]);
        else {
            const next = value.slice();
            next[idx] = banner;
            onChange(next);
        }
        setEditing(null);
    };
    const askDelete = (banner) => setConfirmDelete(banner);

    const doDelete = async () => {
        if (!confirmDelete) return;
        const next = value.filter((b) => b.id !== confirmDelete.id);
        try {
            const { data } = await apiFetch.put("/admin/settings/banners", { value: next });
            onChange(Array.isArray(data?.value) ? data.value : next);
            toast.success("Banner eliminado");
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo eliminar");
            throw e;
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await apiFetch.put("/admin/settings/banners", { value });
            toast.success("Banners guardados");
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="rounded-xl border border-zinc-200 bg-white p-5" data-testid="admin-banners">
            <header className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Megaphone className="w-4 h-4 text-zinc-500" />
                    <h3 className="text-sm font-semibold text-zinc-900">Banners globales</h3>
                </div>
                <Button
                    onClick={() => setEditing({})}
                    variant="outline"
                    data-testid="new-banner"
                >
                    <Plus className="w-4 h-4 mr-1.5" /> Nuevo banner
                </Button>
            </header>
            <p className="mt-1 text-xs text-zinc-500">
                Mensajes que aparecen en la parte superior del sitio o del panel de
                comerciantes. Puedes activar/desactivar, fijar ventana de fechas y
                añadir un enlace opcional.
            </p>
            <div className="mt-4 space-y-2">
                {value.length === 0 && (
                    <p className="text-xs text-zinc-400 italic">Sin banners configurados.</p>
                )}
                {value.map((b) => (
                    <div
                        key={b.id}
                        data-testid={`banner-row-${b.id}`}
                        className="rounded-lg border border-zinc-200 px-4 py-3 flex items-start gap-3"
                    >
                        <span
                            className={`mt-0.5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border ${
                                b.type === "warning"
                                    ? "bg-amber-50 text-amber-700 border-amber-200"
                                    : b.type === "success"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : "bg-indigo-50 text-indigo-700 border-indigo-200"
                            }`}
                        >
                            {b.type || "info"}
                        </span>
                        <div className="flex-1 min-w-0">
                            <p className={`text-sm ${b.enabled === false ? "text-zinc-400 line-through" : "text-zinc-800"}`}>
                                {b.message}
                            </p>
                            <p className="mt-0.5 text-xs text-zinc-500">
                                Scope: {b.scope || "home"}
                                {b.startsAt && ` · desde ${b.startsAt}`}
                                {b.endsAt && ` · hasta ${b.endsAt}`}
                                {b.link && ` · enlace ↗`}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                onClick={() => setEditing(b)}
                                className="text-zinc-400 hover:text-indigo-600"
                                aria-label="Editar"
                            >
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => askDelete(b)}
                                className="text-zinc-400 hover:text-red-600"
                                aria-label="Eliminar"
                                data-testid={`delete-banner-${b.id}`}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-4 flex justify-end">
                <Button
                    onClick={save}
                    disabled={saving}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="save-banners"
                >
                    {saving ? "Guardando…" : "Guardar lista"}
                </Button>
            </div>

            <BannerFormDialog
                banner={editing}
                onClose={() => setEditing(null)}
                onSave={upsert}
            />

            <ConfirmDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
                title="¿Eliminar este banner?"
                description={`"${confirmDelete?.message || ""}" se eliminará permanentemente y dejará de mostrarse en el sitio.`}
                confirmLabel="Eliminar"
                confirmingLabel="Eliminando…"
                tone="destructive"
                onConfirm={doDelete}
            />
        </section>
    );
}

function BannerFormDialog({ banner, onClose, onSave }) {
    const open = banner !== null && banner !== undefined;
    const [form, setForm] = useState({});

    useEffect(() => {
        if (!open) return;
        setForm({
            id: banner.id || "",
            message: banner.message || "",
            type: banner.type || "info",
            scope: banner.scope || "home",
            startsAt: banner.startsAt || "",
            endsAt: banner.endsAt || "",
            link: banner.link || "",
            linkLabel: banner.linkLabel || "",
            enabled: banner.enabled === undefined ? true : !!banner.enabled,
        });
    }, [banner, open]);

    const set = (k) => (e) => {
        const v = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
        setForm((p) => ({ ...p, [k]: v }));
    };

    const submit = (e) => {
        e.preventDefault();
        if (!form.message?.trim()) {
            toast.error("El mensaje es obligatorio");
            return;
        }
        onSave({
            ...form,
            id: form.id || undefined,
            message: form.message.trim(),
            link: form.link.trim() || null,
            linkLabel: form.linkLabel.trim() || null,
            startsAt: form.startsAt.trim() || null,
            endsAt: form.endsAt.trim() || null,
        });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{form.id ? "Editar banner" : "Nuevo banner"}</DialogTitle>
                    <DialogDescription>
                        Aparecerá en home, dashboard, o ambos según el scope que elijas. No olvides hacer clic en "Guardar lista" después de editar.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="bn-msg">Mensaje</Label>
                        <Textarea
                            id="bn-msg"
                            value={form.message || ""}
                            onChange={set("message")}
                            placeholder="Mantenimiento programado el viernes a las 22:00 h."
                            maxLength={280}
                            rows={3}
                            required
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="bn-type">Tipo</Label>
                            <select
                                id="bn-type"
                                value={form.type || "info"}
                                onChange={set("type")}
                                className="w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                            >
                                <option value="info">Info (azul)</option>
                                <option value="warning">Warning (ámbar)</option>
                                <option value="success">Success (verde)</option>
                            </select>
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="bn-scope">Scope</Label>
                            <select
                                id="bn-scope"
                                value={form.scope || "home"}
                                onChange={set("scope")}
                                className="w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                            >
                                <option value="home">Home (sitio público)</option>
                                <option value="dashboard">Dashboard (comerciantes)</option>
                                <option value="both">Ambos</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="bn-start">Desde (ISO 8601)</Label>
                            <Input
                                id="bn-start"
                                value={form.startsAt || ""}
                                onChange={set("startsAt")}
                                placeholder="2026-05-15T00:00:00Z"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="bn-end">Hasta (ISO 8601)</Label>
                            <Input
                                id="bn-end"
                                value={form.endsAt || ""}
                                onChange={set("endsAt")}
                                placeholder="2026-05-20T23:59:59Z"
                            />
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="bn-link">Enlace (opcional)</Label>
                        <Input
                            id="bn-link"
                            value={form.link || ""}
                            onChange={set("link")}
                            placeholder="https://… o /ruta-interna"
                            maxLength={500}
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="bn-linkLabel">Texto del enlace (opcional)</Label>
                        <Input
                            id="bn-linkLabel"
                            value={form.linkLabel || ""}
                            onChange={set("linkLabel")}
                            placeholder="Saber más"
                            maxLength={40}
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={!!form.enabled} onChange={set("enabled")} />
                        Activo
                    </label>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
                        <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                            {form.id ? "Aplicar cambios" : "Añadir a la lista"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function CategoriesTable() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | "new" | category object
    const [open, setOpen] = useState(false);
    const [confirming, setConfirming] = useState(null);

    const load = async (query = "") => {
        setLoading(true);
        try {
            const { data: r } = await apiFetch.get("/admin/categories", {
                params: { q: query || undefined, limit: 200 },
            });
            setData(r);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const onNew = () => {
        setEditing("new");
        setOpen(true);
    };
    const onEdit = (cat) => {
        setEditing(cat);
        setOpen(true);
    };
    const onDelete = (cat) => {
        const used = (cat.usage?.stores || 0) + (cat.usage?.products || 0);
        setConfirming({
            title: "¿Eliminar categoría?",
            description: used
                ? `La categoría "${cat.name}" está en uso por ${cat.usage.stores} tienda(s) y ${cat.usage.products} producto(s). Si la eliminas, esos registros quedarán huérfanos.`
                : `Se eliminará la categoría "${cat.name}". Esta acción no se puede deshacer.`,
            confirmLabel: "Eliminar",
            confirmingLabel: "Eliminando…",
            tone: "destructive",
            onConfirm: async () => {
                try {
                    await apiFetch.delete(`/admin/categories/${cat.id}`, {
                        params: used ? { force: true } : undefined,
                    });
                    toast.success("Categoría eliminada");
                    load(q);
                } catch (e) {
                    toast.error(e.response?.data?.detail || "No se pudo eliminar");
                    throw e;
                }
            },
        });
    };
    const onSaved = () => {
        setOpen(false);
        setEditing(null);
        load(q);
    };

    return (
        <div data-testid="admin-categories-table">
            <div className="flex flex-wrap items-center gap-3 justify-between">
                <SearchInput
                    value={q}
                    onChange={setQ}
                    onSubmit={() => load(q)}
                    placeholder="Buscar por nombre o slug…"
                    testid="admin-categories-search"
                />
                <div className="flex items-center gap-2">
                    <ExportCsvButton kind="categories" q={q} />
                    <Button
                        onClick={onNew}
                        data-testid="admin-new-category"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                        <Plus className="w-4 h-4 mr-1.5" /> Nueva categoría
                    </Button>
                </div>
            </div>
            <p className="mt-3 text-xs text-zinc-500">{data.total} categorías</p>
            <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-50 text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Categoría</th>
                            <th className="text-left px-4 py-3">Slug</th>
                            <th className="text-left px-4 py-3">Descripción</th>
                            <th className="text-left px-4 py-3">Uso</th>
                            <th className="text-right px-4 py-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                        {loading ? (
                            <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400">Cargando…</td></tr>
                        ) : data.items.length === 0 ? (
                            <tr><td colSpan={5} className="px-4 py-8 text-center text-zinc-400">Sin resultados</td></tr>
                        ) : data.items.map((c) => {
                            const Icon = CATEGORY_ICONS[c.icon] || Tag;
                            return (
                                <tr key={c.id} data-testid={`admin-category-row-${c.slug}`}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-indigo-50 grid place-items-center">
                                                <Icon className="w-4 h-4 text-indigo-600" />
                                            </div>
                                            <span className="font-medium text-zinc-900">{c.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-zinc-600 font-mono text-xs">{c.slug}</td>
                                    <td className="px-4 py-3 text-zinc-600 max-w-md truncate">{c.description}</td>
                                    <td className="px-4 py-3 text-zinc-500 text-xs">
                                        {c.usage?.stores || 0} tiendas · {c.usage?.products || 0} productos
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button
                                                data-testid={`admin-edit-category-${c.slug}`}
                                                onClick={() => onEdit(c)}
                                                className="text-zinc-400 hover:text-indigo-600"
                                                aria-label="Editar"
                                            >
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                            <button
                                                data-testid={`admin-delete-category-${c.slug}`}
                                                onClick={() => onDelete(c)}
                                                className="text-zinc-400 hover:text-red-600"
                                                aria-label="Eliminar"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <CategoryFormDialog
                open={open}
                onOpenChange={(o) => {
                    setOpen(o);
                    if (!o) setEditing(null);
                }}
                category={editing === "new" ? null : editing}
                onSaved={onSaved}
            />

            <ConfirmDialog
                open={!!confirming}
                onOpenChange={(o) => !o && setConfirming(null)}
                {...(confirming || {})}
            />
        </div>
    );
}

function CategoryFormDialog({ open, onOpenChange, category, onSaved }) {
    const isEdit = !!category;
    const [name, setName] = useState("");
    const [slug, setSlug] = useState("");
    const [slugTouched, setSlugTouched] = useState(false);
    const [icon, setIcon] = useState("Tag");
    const [description, setDescription] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setName(category?.name || "");
        setSlug(category?.slug || "");
        setIcon(category?.icon || "Tag");
        setDescription(category?.description || "");
        setSlugTouched(!!category);
    }, [open, category]);

    const onNameChange = (v) => {
        setName(v);
        if (!slugTouched) setSlug(slugify(v));
    };
    const onSlugChange = (v) => {
        setSlugTouched(true);
        setSlug(v.toLowerCase().replace(/[^a-z0-9-]/g, ""));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !slug.trim() || !icon.trim()) {
            toast.error("Nombre, slug e icono son obligatorios");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                slug: slug.trim(),
                icon: icon.trim(),
                description: description.trim(),
            };
            if (isEdit) {
                await apiFetch.put(`/admin/categories/${category.id}`, payload);
                toast.success("Categoría actualizada");
            } else {
                await apiFetch.post("/admin/categories", payload);
                toast.success("Categoría creada");
            }
            onSaved?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    const PreviewIcon = CATEGORY_ICONS[icon] || Tag;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Editar categoría" : "Nueva categoría"}</DialogTitle>
                    <DialogDescription>
                        Las categorías se muestran en la portada y se usan para clasificar tiendas y productos.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="cat-name">Nombre</Label>
                        <Input
                            id="cat-name"
                            data-testid="cat-form-name"
                            value={name}
                            onChange={(e) => onNameChange(e.target.value)}
                            placeholder="Ej. Joyería"
                            maxLength={60}
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="cat-slug">Slug</Label>
                        <Input
                            id="cat-slug"
                            data-testid="cat-form-slug"
                            value={slug}
                            onChange={(e) => onSlugChange(e.target.value)}
                            placeholder="joyeria"
                            maxLength={60}
                            required
                        />
                        <p className="text-xs text-zinc-500">
                            Solo minúsculas, números y guiones. Se usa en URLs y filtros.
                        </p>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="cat-icon">Icono</Label>
                        <div className="flex gap-2 items-start">
                            <div className="w-10 h-10 rounded-lg bg-indigo-50 grid place-items-center shrink-0">
                                <PreviewIcon className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="flex-1 space-y-1.5">
                                <select
                                    id="cat-icon"
                                    data-testid="cat-form-icon"
                                    value={CATEGORY_ICON_NAMES.includes(icon) ? icon : ""}
                                    onChange={(e) => setIcon(e.target.value)}
                                    className="w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                                >
                                    <option value="">— Personalizado —</option>
                                    {CATEGORY_ICON_NAMES.map((n) => (
                                        <option key={n} value={n}>{n}</option>
                                    ))}
                                </select>
                                <Input
                                    value={icon}
                                    onChange={(e) => setIcon(e.target.value)}
                                    placeholder="Nombre del icono lucide (ej. Shirt)"
                                    maxLength={40}
                                />
                            </div>
                        </div>
                        <p className="text-xs text-zinc-500">
                            Lista de iconos: lucide.dev. Si el nombre no existe se mostrará el icono por defecto.
                        </p>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="cat-desc">Descripción</Label>
                        <Textarea
                            id="cat-desc"
                            data-testid="cat-form-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Breve descripción visible en la portada"
                            maxLength={200}
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={saving}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            data-testid="cat-form-submit"
                            disabled={saving}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear categoría"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function ConfirmDialog({
    open,
    onOpenChange,
    title,
    description,
    confirmLabel = "Confirmar",
    confirmingLabel = "Procesando…",
    cancelLabel = "Cancelar",
    tone = "default", // "default" | "destructive"
    onConfirm,
    reason, // optional: { label, placeholder, required, defaultValue }
}) {
    const [busy, setBusy] = useState(false);
    const [reasonValue, setReasonValue] = useState("");

    useEffect(() => {
        if (open) {
            setReasonValue(reason?.defaultValue || "");
            setBusy(false);
        }
    }, [open, reason?.defaultValue]);

    const close = (o) => {
        if (busy) return;
        onOpenChange(o);
    };

    const handleConfirm = async () => {
        if (busy) return;
        if (reason?.required && !reasonValue.trim()) {
            toast.error(`${reason.label || "Motivo"} es obligatorio`);
            return;
        }
        setBusy(true);
        try {
            await onConfirm?.(reasonValue);
            onOpenChange(false);
        } catch {
            /* Consumer should toast; keep dialog open for retry. */
        } finally {
            setBusy(false);
        }
    };

    const confirmClass =
        tone === "destructive"
            ? "bg-red-600 hover:bg-red-700 text-white"
            : "bg-indigo-600 hover:bg-indigo-700 text-white";

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {description && (
                        <DialogDescription>{description}</DialogDescription>
                    )}
                </DialogHeader>
                {reason && (
                    <div className="space-y-1.5">
                        <Label htmlFor="confirm-reason">{reason.label}</Label>
                        <Textarea
                            id="confirm-reason"
                            value={reasonValue}
                            onChange={(e) => setReasonValue(e.target.value)}
                            placeholder={reason.placeholder}
                            rows={3}
                            maxLength={300}
                            disabled={busy}
                            data-testid="confirm-reason"
                        />
                    </div>
                )}
                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                    >
                        {cancelLabel}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleConfirm}
                        disabled={busy}
                        className={confirmClass}
                        data-testid="confirm-action"
                    >
                        {busy ? confirmingLabel : confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ExportCsvButton({ kind, q, label = "Exportar CSV" }) {
    const [busy, setBusy] = useState(false);
    const onClick = async () => {
        setBusy(true);
        try {
            const res = await apiFetch.get(`/admin/export/${kind}`, {
                params: { q: q || undefined },
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
            const today = new Date().toISOString().slice(0, 10);
            const a = document.createElement("a");
            a.href = url;
            a.download = `tiendify_${kind}_${today}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            toast.success(`CSV de ${kind} descargado`);
        } catch (e) {
            toast.error("No se pudo exportar");
        } finally {
            setBusy(false);
        }
    };
    return (
        <Button
            onClick={onClick}
            disabled={busy}
            variant="outline"
            className="border-zinc-200"
            data-testid={`admin-export-${kind}`}
        >
            <Download className="w-4 h-4 mr-1.5" />
            {busy ? "Exportando…" : label}
        </Button>
    );
}

function SearchInput({ value, onChange, onSubmit, placeholder, testid }) {
    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}
            className="flex gap-2 max-w-md"
        >
            <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    data-testid={testid}
                    className="pl-9 h-10"
                />
            </div>
            <Button type="submit" variant="outline" className="border-zinc-200">
                Buscar
            </Button>
        </form>
    );
}

function RoleBadge({ role }) {
    const map = {
        admin: "bg-indigo-100 text-indigo-700 border-indigo-200",
        merchant: "bg-amber-100 text-amber-700 border-amber-200",
        customer: "bg-zinc-100 text-zinc-700 border-zinc-200",
    };
    const cls = map[role] || map.customer;
    return (
        <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full border ${cls}`}>
            {role}
        </span>
    );
}
